/**
 * `<RepairsPage>` — CHEATSHEET.app's Repairs / Rest / Full Repair page, shown to players and the
 * GM alike. Its job at the table: a player reads it during a Rest to decide how to spend their
 * mech's Repairs, without opening the book. Sourced from the Lancer Core Rulebook p.82
 * ("REPAIRS AND REST", "REPAIRING DESTROYED MECHS", "FULL REPAIR"), p.71 (STABILIZE) and p.36
 * (the Everest's "Replaceable parts" trait), paraphrased and page-cited per block.
 *
 * Deliberately says nothing about how many Repairs anyone has left: that lives on the encounter
 * and the roster (`printedMechs[mechId].repairsRemaining`), not in static copy. The mech sheet's
 * WEAR & REPAIRS block (`MechWearBlock.tsx`) shows it and spends it.
 *
 * The block headings are the render test's stable contract — don't rename them.
 */
export function RepairsPage() {
  return (
    <div className="gms-cheatsheet-page">
      <h2 className="gms-cheatsheet-page-title">REPAIRS AND REST</h2>

      <section className="gms-cheatsheet-block">
        <h3>REPAIR CAP</h3>
        <p>
          <strong>REPAIRS</strong> are your mech's supply of parts, tools and resilience. Its <strong>REPAIR CAP</strong> is how
          many you start a mission with: set by your frame, <strong>+1 for every 2 points of HULL</strong>. You spend them in
          combat and at Rests, and you only get them back at a <strong>FULL REPAIR</strong>.
        </p>
        <p>The WEAR &amp; REPAIRS block on your mech sheet shows how many your mech has left.</p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.82</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>IN COMBAT</h3>
        <ul className="gms-cheatsheet-list">
          <li>
            <strong>STABILIZE</strong> (full action) can <strong>mark 1 REPAIR to restore all HP</strong>. Its other option — cool
            the mech, clearing all heat and EXPOSED — costs nothing.
          </li>
          <li>Some systems spend REPAIRS too; their text says so.</li>
        </ul>
        <p className="gms-cheatsheet-cite">Core Rulebook p.71, p.82</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>AT A REST</h3>
        <p>
          A Rest is <strong>at least an hour</strong> of uninterrupted downtime or light work — making camp, routine maintenance.
        </p>
        <div className="gms-cheatsheet-rules-grid">
          <div className="gms-cheatsheet-rule-card">
            <span className="gms-cheatsheet-rule-kicker">Free, automatically</span>
            <p>
              Clear all <strong>heat</strong>. Clear every <strong>status and condition</strong> on your mech. Your pilot restores{" "}
              <strong>half their HP</strong> and clears DOWN AND OUT.
            </p>
          </div>
          <div className="gms-cheatsheet-rule-card">
            <span className="gms-cheatsheet-rule-kicker">Costs REPAIRS</span>
            <p>Everything on the price list below. Spend as many as you have, in any combination.</p>
          </div>
        </div>
        <table className="gms-cheatsheet-table">
          <thead>
            <tr>
              <th>Cost</th>
              <th>What it buys</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">1 REPAIR</th>
              <td>Restore your mech to full HP</td>
            </tr>
            <tr>
              <th scope="row">1 REPAIR</th>
              <td>Fix one destroyed weapon or system</td>
            </tr>
            <tr>
              <th scope="row">2 REPAIRS</th>
              <td>
                Restore 1 STRUCTURE — <strong>1 REPAIR on an Everest</strong> (Replaceable parts)
              </td>
            </tr>
            <tr>
              <th scope="row">2 REPAIRS</th>
              <td>Restore 1 STRESS</td>
            </tr>
            <tr>
              <th scope="row">4 REPAIRS</th>
              <td>Restore a destroyed mech (see below)</td>
            </tr>
          </tbody>
        </table>
        <p className="gms-cheatsheet-example">
          E.g. an Everest that ended a fight on 1 HP and 3/4 STRUCTURE: 1 REPAIR for full HP + 1 REPAIR for the structure point = 2
          REPAIRS. On the mech sheet's WEAR &amp; REPAIRS block, once the fight has ended: Restore HP, then +1 Structure.
        </p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.82, p.36</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>DESTROYED MECHS</h3>
        <ul className="gms-cheatsheet-list">
          <li>
            A destroyed mech is <strong>wreckage</strong>: an object that gives hard cover and makes its spaces difficult terrain.
          </li>
          <li>
            If the wreckage can be reached (it didn't melt in a reactor meltdown), it can be restored during a Rest for{" "}
            <strong>4 REPAIRS</strong> — and <strong>anyone can chip in</strong>. It's the only time pilots may share REPAIRS.
          </li>
          <li>
            It comes back at <strong>1 STRUCTURE, 1 STRESS and full HP</strong>. Destroyed weapons and systems stay broken until its
            own REPAIRS fix them.
          </li>
        </ul>
        <p className="gms-cheatsheet-cite">Core Rulebook p.82</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>FULL REPAIR</h3>
        <p>
          <strong>At least 10 hours</strong>, fairly uninterrupted, <strong>somewhere secure</strong>. It resets everything:
        </p>
        <ul className="gms-cheatsheet-list">
          <li>Pilot to full HP, DOWN AND OUT cleared.</li>
          <li>Mech to full HP, STRUCTURE and STRESS — if it's intact or its wreckage can be reached.</li>
          <li>All statuses and conditions cleared; every destroyed weapon and system repaired.</li>
          <li>
            <strong>All REPAIRS back</strong>, and all uses of LIMITED weapons and systems. CP recovered if used.
          </li>
          <li>With the right facilities, print a new mech or rebuild a lost one.</li>
        </ul>
        <p className="gms-cheatsheet-cite">Core Rulebook p.82</p>
      </section>
    </div>
  );
}
